/**
 * The record card's vocabulary — one glyph, one name, one tone per kind, plus
 * the severity and status tones — in the one place BOTH apps can read.
 *
 * ## Why it moved
 *
 * All of this was the console's (`webapp/src/client/components/chrome.tsx`).
 * The phone cannot import from `webapp/`: the dependency runs the other way
 * (`webapp` depends on `backend`, `tests/guards/backendImportPath.test.ts`
 * polices how). So for one `RecordCard` to serve the console and the WebView
 * SPA, the tables have to live here and the console has to read them back.
 * `chrome.tsx` keeps every one of its old export names and delegates; no
 * console call site changed, and nothing about the console's appearance did
 * either.
 *
 * ## The one thing that is NOT verbatim: the colours are hex here
 *
 * The console writes colour as `C.red` and nothing under `webapp/src/client`
 * may contain a hex literal (`tokens.test.ts` scans for them). That rule is
 * intact — `chrome.tsx` still holds no hex. But `tokens.ts` is a console file,
 * and importing it here would be exactly the backwards dependency this move
 * exists to avoid, so each value below is its token RESOLVED, with the token
 * named beside it. The pairs are listed once, at TOKENS, so there is one place
 * to look rather than six.
 *
 * A copy that only a comment asks you to keep in step is a copy that drifts,
 * and a drift here splits the console's colours from the phone's with every
 * suite green — `tokens.test.ts` walks `webapp/src/client` and so cannot see
 * this file at all. `webapp/tests/guards/recordChromeMatchesTokens.test.ts`
 * is the thing that actually fails: it can import BOTH trees, and it checks
 * every value below against the token named beside it, that the tables draw
 * nothing but those values, and that no colour is written outside TOKENS.
 *
 * Everything else — the SVG path data, the label strings, the pairings — is
 * pasted byte for byte out of `chrome.tsx` (`KIND_ICON`, `KIND_LABEL`,
 * `KIND_LABEL_PLURAL`, `KIND_CITE`, `SEVERITY`, `STATUS_CHIP`). Do not tidy a
 * path string: a "tidied" path is a differently-shaped glyph.
 */

/**
 * The four-kind vocabulary, in the console's own order. Structurally identical
 * to `ItemRecordKind` (`backend/src/backend/services/itemMovement.ts`) and to
 * the console's `ConsoleItemKind`, which aliases it — spelled out here rather
 * than imported so `src/shared` does not reach into a backend service.
 */
export const RECORD_KINDS = ["log", "observation", "task", "punch"] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

/** A kind's severity band, as the console names them. */
export type RecordSeverity = "low" | "medium" | "high";

export interface RecordTone {
  ink: string;
  bg: string;
}

export interface RecordChipTone extends RecordTone {
  label: string;
}

/**
 * `webapp/src/client/tokens.ts`, resolved. Named so a reader can check a value
 * against the console's palette without opening two files, and so a token
 * rename has one landing site here instead of six.
 */
const TOKENS = {
  /** `C.sub` */ sub: "#6E6357",
  /** `C.faint` */ faint: "#F1E9D8",
  /** `C.green` */ green: "#00B877",
  /** `C.greenSoft` */ greenSoft: "#DFF3EA",
  /** `C.amber` */ amber: "#C48A00",
  /** `C.amberSoft` */ amberSoft: "#F7EBCF",
  /** `C.red` */ red: "#D94B3B",
  /** `C.redSoft` */ redSoft: "#FBE3DC",
  /** `C.blue` */ blue: "#0077C4",
  /** `C.blueSoft` */ blueSoft: "#E1EFF8",
} as const;

/** Icon paths verbatim from the design's `KIND_ICON` table. */
export const RECORD_KIND_GLYPHS = {
  log: { d1: "M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z", d2: "M8 13h8M8 17h6" },
  observation: {
    d1: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z",
    d2: "M12 9a3 3 0 100 6 3 3 0 000-6Z",
  },
  task: { d1: "M3.5 3.5h17v17h-17z", d2: "m8 12 3 3 5-6" },
  punch: { d1: "M12 4 2.5 20h19L12 4Z", d2: "M12 10v4M12 17.4v.01" },
} as const satisfies Record<RecordKind, { d1: string; d2: string }>;

export const RECORD_KIND_LABELS = {
  log: "Daily log",
  observation: "Observation",
  task: "Task",
  punch: "Punch",
} as const satisfies Record<RecordKind, string>;

/**
 * The same labels PLURALIZED, for the blocks that count a SET of records
 * rather than name one of them: "Daily logs 4" reads as a count where
 * "Daily log 4" reads as a name. `MemberUsage`'s coverage block drew this
 * distinction first in a table of its own; this is that table, spelled once.
 */
export const RECORD_KIND_LABELS_PLURAL = {
  log: "Daily logs",
  observation: "Observations",
  task: "Tasks",
  punch: "Punch items",
} as const satisfies Record<RecordKind, string>;

/**
 * Kind colors for an INLINE citation chip — the console's `KIND_CITE`.
 *
 * The console's `KindChip` is deliberately monochrome, because it labels a row
 * that is already visually a row. A chip embedded in running prose has no such
 * frame: without color it reads as emphasis rather than as a control. Every
 * value here is a token the console already uses elsewhere — no new palette.
 *
 * `walk` is not a record kind and the phone has no use for it. It stays
 * because the console's `citePalette()` reads it.
 */
export const RECORD_KIND_TONES = {
  task: { ink: TOKENS.blue, bg: TOKENS.blueSoft },
  observation: { ink: TOKENS.amber, bg: TOKENS.amberSoft },
  punch: { ink: TOKENS.red, bg: TOKENS.redSoft },
  log: { ink: TOKENS.blue, bg: TOKENS.blueSoft },
  walk: { ink: TOKENS.sub, bg: TOKENS.faint },
} as const satisfies Record<RecordKind | "walk", RecordTone>;

export const RECORD_SEVERITY_TONES = {
  high: { label: "High", ink: TOKENS.red, bg: TOKENS.redSoft },
  medium: { label: "Medium", ink: TOKENS.amber, bg: TOKENS.amberSoft },
  low: { label: "Low", ink: TOKENS.sub, bg: TOKENS.faint },
} as const satisfies Record<RecordSeverity, RecordChipTone>;

/** The design's status chips: open amber, ready-for-review green, logged
 *  grey, plus the two the design's demo data never reaches. */
export const RECORD_STATUS_TONES = {
  open: { label: "Open", ink: TOKENS.amber, bg: TOKENS.amberSoft },
  in_progress: { label: "In progress", ink: TOKENS.blue, bg: TOKENS.blueSoft },
  ready_for_review: { label: "Ready for review", ink: TOKENS.green, bg: TOKENS.greenSoft },
  closed: { label: "Closed", ink: TOKENS.green, bg: TOKENS.greenSoft },
  logged: { label: "Logged", ink: TOKENS.sub, bg: TOKENS.faint },
} as const satisfies Record<string, RecordChipTone>;

export type RecordStatus = keyof typeof RECORD_STATUS_TONES;

export function kindGlyph(kind: RecordKind): { d1: string; d2?: string } {
  return RECORD_KIND_GLYPHS[kind];
}

export function kindLabel(kind: RecordKind, plural = false): string {
  return plural ? RECORD_KIND_LABELS_PLURAL[kind] : RECORD_KIND_LABELS[kind];
}

export function kindTone(kind: RecordKind): RecordTone {
  return RECORD_KIND_TONES[kind];
}

export function severityTone(level: RecordSeverity): RecordChipTone {
  return RECORD_SEVERITY_TONES[level];
}

/**
 * A status's chip tone. `logged` is the fallback rather than a throw: a newer
 * backend's status must render as a quiet grey chip, never as a crash or a
 * blank. The console's `STATUS_CHIP` had no fallback because its projection
 * normalizes every model into these five; the phone reads raw model statuses,
 * so it needs one.
 */
export function statusTone(status: string): RecordChipTone {
  return RECORD_STATUS_TONES[status as RecordStatus] ?? RECORD_STATUS_TONES.logged;
}
